"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { LABEL, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  columnNames,
  DatasetInput,
  useDatasetInput,
} from "@/components/tools/stats-regression/dataset-input";
import { XYPlot } from "@/components/tools/stats-regression/xy-plot";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { normalQuantile } from "@/lib/stats/distributions";
import {
  formatPRelation,
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  completeRows,
  type Dataset,
  take,
} from "@/lib/tool-kit/stats-regression/dataset";
import {
  type PartialReport,
  partialCorrelation,
} from "@/lib/tool-kit/stats-regression/partial";
import { apaBounded, apaPClause } from "@/lib/tool-kit/stats-research/format";

/** English strings. A translated content file overrides these through `ui`. */
export const PARTIAL_CORRELATION_CALCULATOR_STRINGS = {
  dataLabel: "Data — one row per case, one column per variable",
  dataHint:
    "Paste from a spreadsheet (with a header row if you have one), or separate values with tabs, commas, semicolons or spaces. Blank cells and NA count as missing.",
  placeholder: "Hours\tScore\tGPA\n6\t65\t2.7\n9\t79\t3.0",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  readSummary: "Read {rows} × {columns}.",
  rowCount: "one: {count} row|other: {count} rows",
  columnCount: "one: {count} column|other: {count} columns",
  moreRows: "one: … and {count} more row|other: … and {count} more rows",
  missingCells:
    "one: {count} cell is blank or NA.|other: {count} cells are blank or NA.",
  variableName: "Variable {number}",
  example:
    "Hours\tScore\tGPA\n6\t65\t2.7\n9\t79\t3\n4\t68\t2.3\n7\t65\t2.4\n12\t93\t3.5\n7\t70\t2.6\n12\t82\t3.6\n9\t87\t3.3\n8\t81\t3\n4\t68\t2.5\n4\t72\t2.6\n6\t72\t2.8\n7\t64\t2.3\n5\t76\t2.9\n4\t68\t2.5\n10\t82\t3\n8\t76\t2.9\n15\t92\t3.4\n6\t78\t2.7\n7\t73\t2.7",
  xLabel: "Variable X",
  yLabel: "Variable Y",
  controlsLabel: "Control for (hold constant)",
  levelLabel: "Confidence level",
  level90: "90%",
  level95: "95%",
  level99: "99%",
  needNumeric: "Partial correlation needs at least three numeric columns.",
  needControls: "Tick at least one variable to control for.",
  sameVariable: "Choose two different variables for X and Y.",
  tooFewCases:
    "Not enough complete cases: you need more than {needed} rows that have a value for every selected variable.",
  droppedRows:
    "one: {count} row with a missing value was left out (listwise deletion).|other: {count} rows with missing values were left out (listwise deletion).",
  constantVariable:
    "One of the selected variables does not vary (or is fully explained by the controls), so its partial correlation is undefined.",
  results: "Results",
  partialR: "Partial correlation",
  pValue: "p-value (two-tailed)",
  tStatistic: "t",
  degreesOfFreedom: "Degrees of freedom",
  zeroOrder: "Zero-order r (no controls)",
  casesN: "Cases used (n)",
  confidenceInterval: "{level} confidence interval",
  ciTemplate: "{low} to {high}",
  ciUnavailable: "n too small for an interval",
  summary:
    "Controlling for {controls}, the correlation between {x} and {y} is {partial}, compared with {zero} before controlling.",
  summaryStronger:
    "The controls were masking part of the relationship: holding them constant makes it stronger (a suppressor effect).",
  summaryWeaker:
    "Holding the controls constant weakens the association: part of what looked like a link between {x} and {y} is shared with the controls.",
  summarySame:
    "The controls barely change the relationship: it does not depend on them.",
  significant: "p {p}: statistically significant at the 5% level.",
  notSignificant: "p {p}: not statistically significant at the 5% level.",
  apaLabel: "APA-style result",
  apaTemplate: "partial r({df}) = {r}, {level} CI [{low}, {high}], p {p}",
  apaTemplateNoCi: "partial r({df}) = {r}, p {p}",
  copyResult: "Copy result",
  copied: "Copied!",
  semiTitle: "Semipartial correlations",
  colMeasure: "Measure",
  colValue: "r",
  colSquared: "r²",
  semiX: "{x} with the controls removed, correlated with {y}",
  semiY: "{y} with the controls removed, correlated with {x}",
  semiNote:
    "A semipartial correlation removes the controls from one variable only. Its square is the share of the total variance in the other variable that this one uniquely explains.",
  controlsTitle: "How each control relates to X and Y",
  colControl: "Control",
  colWithX: "r with {x}",
  colWithY: "r with {y}",
  plotTitle: "Added-variable plot: what is left after the controls",
  plotLabel:
    "Scatter plot of {n} cases: the part of {x} not explained by the controls against the part of {y} not explained by them",
  plotNote:
    "Each point is a case. The axes show the residuals of {x} and {y} after regressing each on the controls; the partial correlation is the ordinary correlation of these two columns.",
  axisX: "{x}, controls removed",
  axisY: "{y}, controls removed",
  seriesPoints: "Cases",
  seriesLine: "Least-squares line through the residuals",
  workingTitle: "How it was calculated",
  workResiduals:
    "Regress {x} on {controls}, and {y} on {controls}; keep the residuals e_x and e_y (the parts the controls cannot explain).",
  workCorrelate: "partial r = Pearson r(e_x, e_y) = {r}",
  workFirstOrder:
    "With one control: r = (r_xy − r_xz × r_yz) ÷ √((1 − r_xz²)(1 − r_yz²)) = ({rxy} − {rxz} × {ryz}) ÷ √((1 − {rxz2})(1 − {ryz2})) = {r}",
  workT:
    "t = r × √((n − 2 − k) ÷ (1 − r²)) = {r} × √({df} ÷ {oneMinus}) = {t}, with {df} degrees of freedom (n = {n}, k = {k} control) → p {p}",
  workCi:
    "Interval: tanh(atanh(r) ± z × 1 ÷ √(n − 3 − k)) = tanh(atanh({r}) ± {z} × 1 ÷ √{m}) = {low} to {high}",
  controlSeparator: ", ",
} as const;

type Strings = typeof PARTIAL_CORRELATION_CALCULATOR_STRINGS;

const LEVELS = { "0.9": 0.9, "0.95": 0.95, "0.99": 0.99 } as const;
type LevelKey = keyof typeof LEVELS;

function interval(
  report: PartialReport,
  num: (value: number) => string,
  strings: Strings
): string {
  return report.ci
    ? t(strings.ciTemplate, { low: num(report.ci[0]), high: num(report.ci[1]) })
    : strings.ciUnavailable;
}

function summaryText(
  report: PartialReport,
  names: { x: string; y: string; controls: string },
  num: (value: number) => string,
  strings: Strings
): string {
  const change = Math.abs(report.partial) - Math.abs(report.zeroOrder);
  let tail: string = strings.summarySame;
  if (change > 0.05) {
    tail = strings.summaryStronger;
  } else if (change < -0.05) {
    tail = t(strings.summaryWeaker, names);
  }
  return `${t(strings.summary, {
    ...names,
    partial: num(report.partial),
    zero: num(report.zeroOrder),
  })} ${tail}`;
}

function Working({
  report,
  names,
  fmt,
  strings,
}: {
  report: PartialReport;
  names: { x: string; y: string; controls: string };
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const z = num(normalQuantile(1 - (1 - report.level) / 2));
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Explanation>{t(strings.workResiduals, names)}</Explanation>
      <Formula>{t(strings.workCorrelate, { r: num(report.partial) })}</Formula>
      {report.firstOrder ? (
        <Formula>
          {t(strings.workFirstOrder, {
            rxy: num(report.zeroOrder),
            rxz: num(report.firstOrder.rxz),
            ryz: num(report.firstOrder.ryz),
            rxz2: num(report.firstOrder.rxz ** 2),
            ryz2: num(report.firstOrder.ryz ** 2),
            r: num(report.partial),
          })}
        </Formula>
      ) : null}
      <Formula>
        {t(strings.workT, {
          r: num(report.partial),
          df: fmt(report.df),
          oneMinus: num(1 - report.partial ** 2),
          t: num(report.t),
          n: fmt(report.n),
          k: fmt(report.controls),
          p: formatPRelation(fmt, report.p),
        })}
      </Formula>
      {report.ci ? (
        <Formula>
          {t(strings.workCi, {
            r: num(report.partial),
            z,
            m: fmt(report.n - 3 - report.controls),
            low: num(report.ci[0]),
            high: num(report.ci[1]),
          })}
        </Formula>
      ) : null}
    </WorkingPanel>
  );
}

interface Analysis {
  report: PartialReport | null;
  dropped: number;
  message: string | null;
}

function analyse(
  dataset: Dataset,
  picks: { x: number; y: number; controls: number[]; numeric: number },
  level: number,
  strings: Strings,
  fmt: NumberFormatter
): Analysis {
  const { x, y, controls } = picks;
  if (picks.numeric < 3) {
    const message = dataset.columns.length > 0 ? strings.needNumeric : null;
    return { report: null, dropped: 0, message };
  }
  if (x === y) {
    return { report: null, dropped: 0, message: strings.sameVariable };
  }
  if (controls.length === 0) {
    return { report: null, dropped: 0, message: strings.needControls };
  }
  const used = [x, y, ...controls].map((i) => dataset.columns[i].numbers);
  const complete = completeRows(used);
  const [xs, ys, ...zs] = used.map((column) => take(column, complete.rows));
  const report = partialCorrelation(xs, ys, zs, level);
  let message: string | null = null;
  if (report === null) {
    message =
      complete.rows.length < controls.length + 3
        ? t(strings.tooFewCases, { needed: fmt(controls.length + 2) })
        : strings.constantVariable;
  }
  return { report, dropped: complete.dropped, message };
}

function Results({
  report,
  labels,
  controls,
  names,
  levelText,
  fmt,
  strings,
}: {
  report: PartialReport;
  labels: { x: string; y: string; controls: string };
  controls: number[];
  names: string[];
  levelText: string;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const apa = apaLine(report, levelText, fmt, strings);
  const slope = slopeThroughOrigin(report.residualsX, report.residualsY);
  const ends = lineEnds(report.residualsX);
  return (
    <section className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <div aria-live="polite" className="space-y-4">
        <StatGrid>
          <StatCard
            emphasis
            label={strings.partialR}
            value={num(report.partial)}
          />
          <StatCard
            label={strings.pValue}
            value={formatPValue(fmt, report.p)}
          />
          <StatCard
            label={t(strings.confidenceInterval, { level: levelText })}
            value={interval(report, num, strings)}
          />
          <StatCard label={strings.zeroOrder} value={num(report.zeroOrder)} />
          <StatCard label={strings.tStatistic} value={num(report.t)} />
          <StatCard label={strings.degreesOfFreedom} value={fmt(report.df)} />
        </StatGrid>
        <Explanation>{summaryText(report, labels, num, strings)}</Explanation>
        <Explanation>
          {t(report.p < 0.05 ? strings.significant : strings.notSignificant, {
            p: formatPRelation(fmt, report.p),
          })}
        </Explanation>
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-mono text-foreground text-sm">
            <span className="text-muted-foreground">{strings.apaLabel}: </span>
            {apa}
          </p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={apa}
          />
        </div>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.semiTitle}</h3>
        <ResultTable
          head={[strings.colMeasure, strings.colValue, strings.colSquared]}
          rows={[
            [
              t(strings.semiX, labels),
              num(report.semipartialX),
              num(report.semipartialX ** 2),
            ],
            [
              t(strings.semiY, labels),
              num(report.semipartialY),
              num(report.semipartialY ** 2),
            ],
          ]}
          wrapFirstColumn
        />
        <Explanation>{strings.semiNote}</Explanation>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.controlsTitle}</h3>
        <ResultTable
          head={[
            strings.colControl,
            t(strings.colWithX, labels),
            t(strings.colWithY, labels),
          ]}
          rows={controls.map((i, k) => [
            names[i],
            num(report.controlR[k].withX),
            num(report.controlR[k].withY),
          ])}
          wrapFirstColumn
        />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
        <XYPlot
          fmt={fmt}
          label={t(strings.plotLabel, { ...labels, n: fmt(report.n) })}
          series={[
            {
              kind: "points",
              xs: report.residualsX,
              ys: report.residualsY,
              color: 0,
              label: strings.seriesPoints,
            },
            {
              kind: "line",
              xs: ends,
              ys: ends.map((v) => v * slope),
              color: 1,
              label: strings.seriesLine,
            },
          ]}
          xLabel={t(strings.axisX, labels)}
          yLabel={t(strings.axisY, labels)}
        />
        <Explanation>{t(strings.plotNote, labels)}</Explanation>
      </div>
      <Working fmt={fmt} names={labels} report={report} strings={strings} />
    </section>
  );
}

/** The index a choice names, or a default when it is empty or no longer numeric. */
function pick(choice: string, numeric: number[], fallback: number): number {
  const index = Number(choice);
  return choice !== "" && numeric.includes(index) ? index : fallback;
}

export default function PartialCorrelationCalculatorWidget() {
  const strings = useToolUi(PARTIAL_CORRELATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const input = useDatasetInput();
  const [xChoice, setXChoice] = useState("");
  const [yChoice, setYChoice] = useState("");
  const [controlChoice, setControlChoice] = useState<number[] | null>(null);
  const [levelKey, setLevelKey] = useState<LevelKey>("0.95");

  const { dataset } = input;
  const names = columnNames(dataset, strings.variableName);
  const numeric = dataset.columns.flatMap((column, index) =>
    column.isNumeric ? [index] : []
  );
  const xIndex = pick(xChoice, numeric, numeric[0] ?? -1);
  const yIndex = pick(
    yChoice,
    numeric,
    numeric.find((i) => i !== xIndex) ?? -1
  );
  const candidates = numeric.filter((i) => i !== xIndex && i !== yIndex);
  const controls = (controlChoice ?? candidates).filter((i) =>
    candidates.includes(i)
  );
  const toggleControl = (index: number) => {
    setControlChoice(
      controls.includes(index)
        ? controls.filter((i) => i !== index)
        : [...controls, index]
    );
  };

  const { report, dropped, message } = analyse(
    dataset,
    { x: xIndex, y: yIndex, controls, numeric: numeric.length },
    LEVELS[levelKey],
    strings,
    fmt
  );
  const labels = {
    x: names[xIndex] ?? "",
    y: names[yIndex] ?? "",
    controls: controls.map((i) => names[i]).join(strings.controlSeparator),
  };
  const options = numeric.map((i) => ({ value: String(i), label: names[i] }));

  return (
    <>
      <DatasetInput
        example={strings.example}
        fmt={fmt}
        id="pc-data"
        labels={strings}
        locale={locale}
        state={input}
      />

      {numeric.length >= 3 ? (
        <section className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField
              id="pc-x"
              label={strings.xLabel}
              onChange={setXChoice}
              options={options}
              value={String(xIndex)}
            />
            <SelectField
              id="pc-y"
              label={strings.yLabel}
              onChange={setYChoice}
              options={options}
              value={String(yIndex)}
            />
            <SelectField
              id="pc-level"
              label={strings.levelLabel}
              onChange={setLevelKey}
              options={[
                { value: "0.9", label: strings.level90 },
                { value: "0.95", label: strings.level95 },
                { value: "0.99", label: strings.level99 },
              ]}
              value={levelKey}
            />
          </div>
          <fieldset className="space-y-2">
            <legend className={LABEL}>{strings.controlsLabel}</legend>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {candidates.map((i) => (
                <Checkbox
                  checked={controls.includes(i)}
                  key={i}
                  label={names[i]}
                  onChange={() => toggleControl(i)}
                />
              ))}
            </div>
          </fieldset>
          {dropped > 0 ? (
            <Notice>
              {tPlural(strings.droppedRows, locale, dropped, {
                count: fmt(dropped),
              }).trim()}
            </Notice>
          ) : null}
        </section>
      ) : null}

      {message ? <Notice>{message}</Notice> : null}

      {report ? (
        <Results
          controls={controls}
          fmt={fmt}
          labels={labels}
          levelText={fmt(LEVELS[levelKey], { style: "percent" })}
          names={names}
          report={report}
          strings={strings}
        />
      ) : null}
    </>
  );
}

function apaLine(
  report: PartialReport,
  level: string,
  fmt: NumberFormatter,
  strings: Strings
): string {
  const base = {
    df: fmt(report.df),
    r: apaBounded(fmt, report.partial, 2),
    p: apaPClause(fmt, report.p),
  };
  if (!report.ci) {
    return t(strings.apaTemplateNoCi, base);
  }
  return t(strings.apaTemplate, {
    ...base,
    level,
    low: apaBounded(fmt, report.ci[0], 2),
    high: apaBounded(fmt, report.ci[1], 2),
  });
}

function lineEnds(values: readonly number[]): number[] {
  return [Math.min(...values), Math.max(...values)];
}

/** Slope of y on x for residual columns, which both have mean zero. */
function slopeThroughOrigin(
  xs: readonly number[],
  ys: readonly number[]
): number {
  let sxy = 0;
  let sxx = 0;
  for (const [i, x] of xs.entries()) {
    sxy += x * ys[i];
    sxx += x * x;
  }
  return sxx === 0 ? 0 : sxy / sxx;
}
